import { GetServerSideProps, GetServerSidePropsContext } from 'next';

import {
  PropertyCard,
  PropertyListLayout,
  PropertySeo,
} from '@/components/property';

import { AppObject, Property } from '@/types';
import {
  getApiLinkQuery,
  queryToFilter,
  queryToPageNumber,
  transformProperty,
} from '@/utility';

import { IPagination, PropertyProvider } from '@/context/property';
import { DropdownData } from '@/components/ui/dropdown-container';
import { getTokenInfo } from '@/utility-backend/auth';
import { Navbar } from '@/components/common/navbar/default';
import { ReplierListingService } from '@/services/repliers';

export interface PropertySearchPageProps {
  properties: Property[];
  pagination: IPagination;
  staticData?: boolean;
  city?: string;
  neighborhood?: string;
  type?: string;
}

export const PropertyTypes: DropdownData[] = [
  {
    label: 'All Properties',
    value: 'all',
  },
  {
    label: 'Apartment',
    value: 'apartment',
  },
  {
    label: 'Residential',
    value: 'residential',
  },
  {
    label: 'Commercial',
    value: 'commercial',
  },
];

export default function PropertySearchPage({
  properties,
  pagination,
  staticData,
  city,
  neighborhood,
  type,
}: PropertySearchPageProps) {
  if (staticData) {
    return (
      <>
        <Navbar />
        <div id="property-main" className="px-4 md:px-8">
          <div className="grid grid-col-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {properties.map((el) => (
              <PropertyCard property={el} key={el.id} showFavIcon />
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <PropertyProvider pagination={pagination} properties={properties}>
      <PropertySeo
        city={city}
        neighborhood={neighborhood}
        type={type}
        count={pagination.count}
      />
      <PropertyListLayout />
    </PropertyProvider>
  );
}

function redirectionToSubscribe(context: GetServerSidePropsContext) {
  context.res.statusCode = 302;
  context.res.setHeader('Location', '/subscribe');

  return {
    props: {
      properties: [],
      staticData: true,
      pagination: {
        count: 0,
        numPages: 0,
      },
    },
  };
}

export const getServerSideProps: GetServerSideProps<
  PropertySearchPageProps
> = async (context) => {
  // if (!validateToken(context.req.cookies.jdr_token)) {
  //   return {
  //     props: {
  //       properties: [],
  //       pagination: {
  //         count: 0,
  //         numPages: 0,
  //         page: 1,
  //         pageNum: 1,
  //       },
  //     },
  //   };
  // }

  let propertyNumbers: string[] = null;

  if (context.query.propertyNumbers) {
    propertyNumbers = (context.query.propertyNumbers as string)
      .split(',')
      .filter(Boolean);
  }

  let query: AppObject;

  if (propertyNumbers?.length) {
    query = {
      propertyNumber: propertyNumbers,
    };
  } else {
    const filter = queryToFilter(context.query);
    const page = queryToPageNumber(context.query);
    query = getApiLinkQuery(
      filter,
      page,
      context.query.view === 'map' ? 'map' : 'list',
      null
    );

    if (context.query.neighborhoodslug) {
      query.neighborhood = context.query.neighborhoodslug;
    }
    if (context.query.cityslug) {
      const city = (context.query.cityslug as string).replace(
        '-real-estate',
        ''
      );
      query.city = city;
    }

    if (page > 5 && !getTokenInfo(context.req.cookies.jdr_token)?.subscribed) {
      return redirectionToSubscribe(context);
    }
  }

  const client = new ReplierListingService();
  const response = await client.findListings(query);

  const properties = (response?.listings || []).map(transformProperty);

  return {
    props: {
      properties: properties,
      staticData: Boolean(propertyNumbers?.length),
      pagination: {
        count: response.count || 0,
        numPages: response.numPages || 0,
      },
      city: (query.city as string) || null,
      neighborhood: (query.neighborhood as string) || null,
      type: (query.type as string) || null,
    },
  };
};
